/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { ActivityManager } from './manager';

import type { HierarchyNode } from './manager';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/**
 * Ceiling for `cel` (seconds since last campaign exposure): 7 days in seconds.
 * Also the value when the campaign has no exposure history at all, so 604800
 * uniformly means "7+ days ago or never" — a raw elapsed value beyond the cap
 * would otherwise make "never exposed" read as more recent than an old
 * exposure.
 */
export const CEL_MAX_SECONDS = 7 * 24 * 60 * 60;

/**
 * Number of opportunities to retain snapshots for. Mirrors the placement's
 * MAX_STORED_OPPORTUNITIES so a snapshot outlives any impression that can still
 * legitimately reference its opportunity.
 */
const MAX_TRACKED_OPPORTUNITIES = 5;

/**
 * Campaign-level exposure snapshot. Viewable exposures only (epic definition:
 * an exposure counts only if the ad was actually seen), prior to the decision
 * the snapshot was taken for.
 */
export type CampaignFrequency = {
  /** Seconds since the last campaign viewable exposure, capped at CEL_MAX_SECONDS. */
  cel: number;
  /** Campaign viewable exposures in the past hour. */
  ceh: number;
  /** Campaign viewable exposures in the past 24 hours. */
  ced: number;
  /** Campaign viewable exposures in the past 7 days. */
  cew: number;
};

/**
 * The auction reference moment a snapshot is computed against. Frozen once per
 * auction so every candidate's windows share the same point.
 */
export type FrequencyContext = {
  now: number;
};

/**
 * Page-level signals captured once per auction opportunity at auction time,
 * alongside the campaign-exposure snapshots. Belong to every impression whose
 * auction was tracked, regardless of campaign type.
 */
export type PageSignals = {
  /** Page Scroll Depth: window.scrollY in pixels at auction time. */
  psd: number;
  /** Session Page Depth: pages visited this browsing session. */
  spd: number;
  /** Page Engagement: 1 when the user has scrolled, clicked or tapped. */
  pen: 0 | 1;
  /** Page Dwell Time: seconds since navigation start at auction time, capped at 300. */
  pdt: number;
};

type OpportunityFrequencies = {
  campaigns: Map<string, CampaignFrequency>;
  /** deliveryId → the owning campaign's snapshot, for impression-time lookup. */
  ads: Map<string, CampaignFrequency>;
  /** Auction-time page signals, shared by every candidate in the opportunity. */
  page?: PageSignals;
};

/**
 * Count timestamps at or after `since`. A full scan, deliberately: campaign
 * arrays are concatenations of per-ad arrays and therefore not globally
 * sorted, and arrays are small (ActivityManager caps at 30 entries per ad per
 * activity type — counts saturate at that cap, a documented undercount for
 * very heavy users).
 */
const countSince = (timestamps: number[], since: number): number => {
  let count = 0;
  for (let i = 0; i < timestamps.length; i++) {
    if (timestamps[i] >= since) {
      count++;
    }
  }
  return count;
};

/**
 * Per-auction snapshots keyed on the opportunity: campaign-exposure values
 * (computed once per (opportunity, campaign) when selection candidates are
 * collected) and the auction's page signals. Both are reused at impression
 * dispatch so the values the model saw are byte-identical to the values
 * recorded against the resulting impression ("what the model sees is what we
 * record"). The impression's own recordImp/recordView never touches this
 * store, so a snapshot cleanly excludes the decision being made.
 *
 * Memoised per opportunity: all placements in one auction share one
 * opportunity, so each unique campaign is computed once regardless of how many
 * placements reference it.
 */
class FrequencyStore {
  private static instance: FrequencyStore | null = null;
  private opportunities: Map<string, OpportunityFrequencies> = new Map();
  private pageSignalsProvider: (() => PageSignals) | null = null;

  private constructor() {}

  public static getInstance(): FrequencyStore {
    if (!FrequencyStore.instance) {
      FrequencyStore.instance = new FrequencyStore();
    }
    return FrequencyStore.instance;
  }

  /**
   * Campaign-level exposure snapshot for an opportunity, computed on first
   * request (at auction time, when selection candidates are collected) and
   * memoised. Counts span ALL the campaign's ads (exposure to the campaign is
   * independent of which ads are currently eligible), and the snapshot is
   * registered under every delivery id so the resulting impression can
   * retrieve it via getFrequencySnapshot. An undefined opportunityId
   * (defensive — the auction flow always mints one before eligible ads are
   * collected) computes without memoisation.
   */
  public getCampaignFrequency(
    opportunityId: string | undefined,
    campaignId: string,
    adDeliveryIds: string[],
    ctx: FrequencyContext,
  ): CampaignFrequency {
    const entry = opportunityId ? this.getOpportunityEntry(opportunityId) : null;
    let frequency = entry?.campaigns.get(campaignId);
    if (!frequency) {
      const hierarchy: HierarchyNode = {
        id: campaignId,
        children: adDeliveryIds.map((id) => ({ id, children: [] })),
      };
      const views = ActivityManager.getInstance().getViewsForEntity(campaignId, hierarchy);

      // Latest exposure via a single scan: the flat-mapped campaign array is
      // per-ad ordered but not globally sorted.
      let latest = 0;
      for (let i = 0; i < views.length; i++) {
        if (views[i] > latest) {
          latest = views[i];
        }
      }

      frequency = {
        cel: latest
          ? Math.min(Math.floor((ctx.now - latest) / 1000), CEL_MAX_SECONDS)
          : CEL_MAX_SECONDS,
        ceh: countSince(views, ctx.now - HOUR_MS),
        ced: countSince(views, ctx.now - DAY_MS),
        cew: countSince(views, ctx.now - WEEK_MS),
      };
      if (entry) {
        entry.campaigns.set(campaignId, frequency);
        for (const deliveryId of adDeliveryIds) {
          entry.ads.set(deliveryId, frequency);
        }
      }
    }
    return frequency;
  }

  /**
   * Campaign-exposure snapshot for the campaign that owns `deliveryId`, or
   * undefined when the delivery was not a selection candidate
   * (learning/monitoring deliveries, or an impression with no tracked
   * auction). Callers that also want page signals should use getImpressionXd.
   */
  public getFrequencySnapshot(
    opportunityId: string | undefined,
    deliveryId: string,
  ): CampaignFrequency | undefined {
    if (!opportunityId) {
      return undefined;
    }
    return this.opportunities.get(opportunityId)?.ads.get(deliveryId);
  }

  /**
   * Record the opportunity's page signals (one snapshot per auction, captured
   * at auction time and shared by every candidate).
   */
  public setPageSignals(opportunityId: string | undefined, signals: PageSignals): void {
    if (!opportunityId) {
      return;
    }
    this.getOpportunityEntry(opportunityId).page = signals;
  }

  public getPageSignals(opportunityId: string | undefined): PageSignals | undefined {
    if (!opportunityId) {
      return undefined;
    }
    return this.opportunities.get(opportunityId)?.page;
  }

  /**
   * Register the live page-signals source. The Page owns the state the
   * signals are computed from (session page depth, engagement latch), so it
   * registers a bound provider at construction. Consumed by
   * captureRenderPageSignals for opportunities minted outside an auction.
   */
  public registerPageSignalsProvider(provider: () => PageSignals): void {
    this.pageSignalsProvider = provider;
  }

  /**
   * Snapshot page signals for a locally-minted opportunity (an auction-less
   * refresh render or a video follow-on ad). These opportunities never pass
   * through the auction-time setPageSignals call, and they have no auction
   * moment to freeze against — render time is the reference point instead,
   * mirroring the advertorial impression path. No-op when no provider is
   * registered, degrading to the previous no-xd behaviour.
   */
  public captureRenderPageSignals(opportunityId: string | undefined): void {
    if (!opportunityId || !this.pageSignalsProvider) {
      return;
    }
    this.setPageSignals(opportunityId, this.pageSignalsProvider());
  }

  /**
   * The full impression `xd` for a delivery, keyed on the auction opportunity:
   *
   * - Page signals (psd/spd/pen/pdt) are captured at auction time and belong to
   *   EVERY impression whose auction we tracked — learning, monitoring and
   *   scoring included.
   * - Campaign-exposure values (cel/ceh/ced/cew) additionally require a
   *   selection: they are present only for delivery ids the model actually
   *   chose (direct/deal candidates).
   *
   * Render-minted opportunities (auction-less refreshes, video follow-ons)
   * carry page signals snapshotted at render time via
   * captureRenderPageSignals. Returns the merge of whichever parts exist, or
   * undefined when neither does (e.g. the snapshot was evicted before the
   * impression dispatched; absent means unknown, never zero).
   */
  public getImpressionXd(
    opportunityId: string | undefined,
    deliveryId: string,
  ): (Partial<CampaignFrequency> & Partial<PageSignals>) | undefined {
    const campaignSnapshot = this.getFrequencySnapshot(opportunityId, deliveryId);
    const pageSignals = this.getPageSignals(opportunityId);
    if (!campaignSnapshot && !pageSignals) {
      return undefined;
    }
    return { ...campaignSnapshot, ...pageSignals };
  }

  private getOpportunityEntry(opportunityId: string): OpportunityFrequencies {
    let entry = this.opportunities.get(opportunityId);
    if (!entry) {
      entry = { campaigns: new Map(), ads: new Map() };
      this.opportunities.set(opportunityId, entry);
      // Insertion-ordered eviction: drop the oldest opportunity beyond the cap.
      if (this.opportunities.size > MAX_TRACKED_OPPORTUNITIES) {
        const oldest = this.opportunities.keys().next().value;
        if (oldest !== undefined) {
          this.opportunities.delete(oldest);
        }
      }
    }
    return entry;
  }

  // This method is used for testing purposes only
  public _reset(): void {
    this.opportunities.clear();
    this.pageSignalsProvider = null;
  }
}

export { FrequencyStore, countSince };
